import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Autocomplete, Chip, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Order, OrderClock } from "../../../api/order";
import collectionApiClient from "../../../api/collection";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSButton from "../../system-design/DSButton";
import DSIconButton from "../../system-design/DSIconButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import {
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableHead,
  DSTableRow,
} from "../../system-design/Table";
import { sectionCardSx } from "../../system-design/sectionCardSx";
import useClockDetailsByIds, {
  ClockInfo,
} from "../../Gifts/GiftOrders/hooks/useClockDetailsByIds";
import useUpdateOrderClocksMutation from "../hooks/useUpdateOrderClocksMutation";
import AssignSerialDialog from "./AssignSerialDialog";
import CollectionPickDialog from "./CollectionPickDialog";

// Mirrors the ClockSku enum in klydo-api (freeSolo allows new SKUs anyway)
const CLOCK_SKUS = [
  "CLOCK-UK-WALNUT",
  "CLOCK-UK-OAK",
  "CLOCK-UK-EBONY",
  "CLOCK-EU-WALNUT",
  "CLOCK-EU-OAK",
  "CLOCK-EU-EBONY",
  "CLOCK-US-WALNUT",
  "CLOCK-US-OAK",
  "CLOCK-US-EBONY",
];

const isOnline = (heartbeat: string) => {
  const diffMinutes =
    (Date.now() - new Date(heartbeat).getTime()) / (1000 * 60);
  return diffMinutes < 5;
};

const AffiliateCollectionCell = ({
  collectionId,
  disabled,
  onPick,
  onClear,
}: {
  collectionId?: string;
  disabled: boolean;
  onPick: () => void;
  onClear: () => void;
}) => {
  const { data: collection } = useQuery({
    queryKey: ["collection", collectionId],
    queryFn: () => collectionApiClient.getCollectionById(collectionId || ""),
    enabled: !!collectionId,
  });

  if (!collectionId) {
    return (
      <DSButton
        size="small"
        variant="text"
        onClick={onPick}
        disabled={disabled}
      >
        Choose collection
      </DSButton>
    );
  }

  return (
    <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <DSBox>
        <DSTypography variant="body2">
          {collection?.name || collectionId}
        </DSTypography>
        {collection?.name && (
          <DSTypography variant="caption" color="text.secondary">
            {collectionId}
          </DSTypography>
        )}
      </DSBox>
      <DSIconButton
        size="small"
        onClick={onPick}
        disabled={disabled}
        title="Change collection"
      >
        <EditIcon fontSize="small" />
      </DSIconButton>
      <DSIconButton
        size="small"
        onClick={onClear}
        disabled={disabled}
        title="Clear collection"
      >
        <CloseIcon fontSize="small" />
      </DSIconButton>
    </DSBox>
  );
};

const ClockCell = ({
  entry,
  clockInfo,
  loadingDetails,
  disabled,
  onClockClick,
  onAssign,
}: {
  entry: OrderClock;
  clockInfo?: ClockInfo;
  loadingDetails: boolean;
  disabled: boolean;
  onClockClick: (clockId: string) => void;
  onAssign: () => void;
}) => {
  if (!entry.clockId) {
    return (
      <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <DSTypography variant="body2" color="text.secondary">
          No serial assigned
        </DSTypography>
        <DSButton
          size="small"
          variant="outlined"
          onClick={onAssign}
          disabled={disabled}
        >
          Assign Serial
        </DSButton>
      </DSBox>
    );
  }

  if (loadingDetails && !clockInfo) {
    return <DSCircularProgress size={18} />;
  }

  return (
    <DSBox
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
      }}
      onClick={() => onClockClick(entry.clockId as string)}
    >
      {clockInfo && (
        <Chip
          size="small"
          label={isOnline(clockInfo.heartbeat) ? "Online" : "Offline"}
          color={isOnline(clockInfo.heartbeat) ? "success" : "default"}
          variant="outlined"
        />
      )}
      <DSBox>
        <DSTypography variant="body2" sx={{ fontWeight: "bold" }}>
          {clockInfo?.currentIdf || clockInfo?.idf || entry.clockId}
        </DSTypography>
        <DSTypography variant="caption" color="text.secondary">
          ID: {entry.clockId}
          {clockInfo?.location &&
            ` • ${clockInfo.location.city}, ${clockInfo.location.country_code}`}
        </DSTypography>
      </DSBox>
    </DSBox>
  );
};

interface OrderClocksSectionProps {
  order: Order;
  onClockClick: (clockId: string) => void;
}

const OrderClocksSection = ({
  order,
  onClockClick,
}: OrderClocksSectionProps) => {
  const clocks = useMemo(() => order.clocks || [], [order.clocks]);
  const clocksNumber = order.clocksNumber || 0;
  const { mutate: updateOrderClocks, isPending: isUpdating } =
    useUpdateOrderClocksMutation(order.id);
  const updateClocks = useCallback(
    (updated: OrderClock[]) => updateOrderClocks({ clocks: updated }),
    [updateOrderClocks],
  );

  // "new" appends a fresh entry; a number assigns a serial to that row
  const [assignTarget, setAssignTarget] = useState<number | "new" | null>(null);
  const [collectionTarget, setCollectionTarget] = useState<number | null>(null);
  const [clocksNumberDraft, setClocksNumberDraft] = useState<string | null>(
    null,
  );

  const atClockLimit = clocks.length >= clocksNumber;

  const handleClocksNumberSave = useCallback(() => {
    const parsed = Number(clocksNumberDraft);
    if (!Number.isInteger(parsed) || parsed < clocks.length) return;
    if (parsed !== clocksNumber) {
      updateOrderClocks({ clocks, clocksNumber: parsed });
    }
    setClocksNumberDraft(null);
  }, [clocksNumberDraft, clocks, clocksNumber, updateOrderClocks]);

  const assignedClockIds = useMemo(
    () =>
      clocks
        .map((clock) => clock.clockId)
        .filter((clockId): clockId is string => !!clockId),
    [clocks],
  );

  const { data: clockDetails, isLoading: isLoadingDetails } =
    useClockDetailsByIds(assignedClockIds);

  const clockDetailsById = useMemo(() => {
    const map = new Map<string, ClockInfo>();
    clockDetails?.forEach((clock) => map.set(clock.id, clock));
    return map;
  }, [clockDetails]);

  const handleAssignSerial = useCallback(
    (clockId: string) => {
      if (assignTarget === null) return;
      const updated =
        assignTarget === "new"
          ? [...clocks, { clockId }]
          : clocks.map((entry, index) =>
              index === assignTarget ? { ...entry, clockId } : entry,
            );
      updateClocks(updated);
      setAssignTarget(null);
    },
    [assignTarget, clocks, updateClocks],
  );

  const handleUnassignSerial = useCallback(
    (rowIndex: number) => {
      updateClocks(
        clocks.map((entry, index) =>
          index === rowIndex ? { ...entry, clockId: undefined } : entry,
        ),
      );
    },
    [clocks, updateClocks],
  );

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      updateClocks(clocks.filter((_, index) => index !== rowIndex));
    },
    [clocks, updateClocks],
  );

  const handleSkuChange = useCallback(
    (rowIndex: number, sku: string | null) => {
      const normalized = sku?.trim().toUpperCase() || undefined;
      if (normalized === (clocks[rowIndex]?.sku || undefined)) return;
      updateClocks(
        clocks.map((entry, index) =>
          index === rowIndex ? { ...entry, sku: normalized } : entry,
        ),
      );
    },
    [clocks, updateClocks],
  );

  const handleCollectionPicked = useCallback(
    (collectionId: string) => {
      if (collectionTarget === null) return;
      updateClocks(
        clocks.map((entry, index) =>
          index === collectionTarget
            ? { ...entry, affiliateCollectionId: collectionId }
            : entry,
        ),
      );
      setCollectionTarget(null);
    },
    [collectionTarget, clocks, updateClocks],
  );

  const handleCollectionCleared = useCallback(
    (rowIndex: number) => {
      updateClocks(
        clocks.map((entry, index) =>
          index === rowIndex
            ? { ...entry, affiliateCollectionId: undefined }
            : entry,
        ),
      );
    },
    [clocks, updateClocks],
  );

  return (
    <DSBox sx={[{ mt: 3, p: 2 }, sectionCardSx]}>
      <DSBox sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <DSTypography
          variant="h6"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <AccessTimeIcon />
          Clock Management
        </DSTypography>
        <DSTypography variant="body2" color="text.secondary">
          ({assignedClockIds.length}/{clocks.length} serials assigned • clocks
          in order:
        </DSTypography>
        {clocksNumberDraft === null ? (
          <>
            <DSTypography variant="body2" color="text.secondary">
              {clocksNumber})
            </DSTypography>
            <DSIconButton
              size="small"
              title="Edit number of clocks"
              disabled={isUpdating}
              onClick={() => setClocksNumberDraft(String(clocksNumber))}
            >
              <EditIcon fontSize="small" />
            </DSIconButton>
          </>
        ) : (
          <TextField
            type="number"
            size="small"
            autoFocus
            value={clocksNumberDraft}
            onChange={(event) => setClocksNumberDraft(event.target.value)}
            onBlur={handleClocksNumberSave}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleClocksNumberSave();
              if (event.key === "Escape") setClocksNumberDraft(null);
            }}
            inputProps={{ min: clocks.length }}
            error={
              !Number.isInteger(Number(clocksNumberDraft)) ||
              Number(clocksNumberDraft) < clocks.length
            }
            helperText={
              Number(clocksNumberDraft) < clocks.length
                ? `Min ${clocks.length} (current clock entries)`
                : undefined
            }
            sx={{ width: 110 }}
          />
        )}
        {isUpdating && <DSCircularProgress size={16} />}
      </DSBox>

      {clocks.length === 0 ? (
        <DSBox
          sx={{
            p: 2,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          <DSTypography variant="body2">No clocks on this order</DSTypography>
        </DSBox>
      ) : (
        <DSTable size="small">
          <DSTableHead>
            <DSTableRow>
              <DSTableCell>Clock</DSTableCell>
              <DSTableCell>SKU</DSTableCell>
              <DSTableCell>Affiliate Collection</DSTableCell>
              <DSTableCell align="right" />
            </DSTableRow>
          </DSTableHead>
          <DSTableBody>
            {clocks.map((entry, index) => (
              // Entries have no id of their own — rows are index-addressed
              // eslint-disable-next-line react/no-array-index-key
              <DSTableRow key={index}>
                <DSTableCell>
                  <ClockCell
                    entry={entry}
                    clockInfo={
                      entry.clockId
                        ? clockDetailsById.get(entry.clockId)
                        : undefined
                    }
                    loadingDetails={isLoadingDetails}
                    disabled={isUpdating}
                    onClockClick={onClockClick}
                    onAssign={() => setAssignTarget(index)}
                  />
                </DSTableCell>
                <DSTableCell>
                  <Autocomplete
                    freeSolo
                    autoSelect
                    size="small"
                    options={CLOCK_SKUS}
                    value={entry.sku || null}
                    onChange={(_, value) => handleSkuChange(index, value)}
                    disabled={isUpdating}
                    sx={{ minWidth: 190 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="SKU"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  />
                </DSTableCell>
                <DSTableCell>
                  <AffiliateCollectionCell
                    collectionId={entry.affiliateCollectionId}
                    disabled={isUpdating}
                    onPick={() => setCollectionTarget(index)}
                    onClear={() => handleCollectionCleared(index)}
                  />
                </DSTableCell>
                <DSTableCell align="right">
                  {entry.clockId && (
                    <>
                      <DSIconButton
                        size="small"
                        onClick={() => setAssignTarget(index)}
                        disabled={isUpdating}
                        title="Change serial"
                      >
                        <SwapHorizIcon fontSize="small" />
                      </DSIconButton>
                      <DSIconButton
                        size="small"
                        onClick={() => handleUnassignSerial(index)}
                        disabled={isUpdating}
                        title="Unassign serial (keep slot)"
                      >
                        <LinkOffIcon fontSize="small" />
                      </DSIconButton>
                    </>
                  )}
                  <DSIconButton
                    size="small"
                    onClick={() => handleDeleteRow(index)}
                    disabled={isUpdating}
                    title="Remove clock from order"
                  >
                    <DeleteIcon fontSize="small" />
                  </DSIconButton>
                </DSTableCell>
              </DSTableRow>
            ))}
          </DSTableBody>
        </DSTable>
      )}

      <DSBox sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <DSButton
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAssignTarget("new")}
          disabled={isUpdating || atClockLimit}
        >
          Add Clock
        </DSButton>
        {atClockLimit && (
          <DSTypography variant="caption" color="text.secondary">
            Order is at its {clocksNumber}-clock limit — raise the number of
            clocks to add more
          </DSTypography>
        )}
      </DSBox>

      <AssignSerialDialog
        open={assignTarget !== null}
        title={
          assignTarget === "new"
            ? "Add Clock"
            : typeof assignTarget === "number" && clocks[assignTarget]?.clockId
              ? "Change Serial"
              : "Assign Serial"
        }
        excludeClockIds={assignedClockIds}
        onClose={() => setAssignTarget(null)}
        onAssign={handleAssignSerial}
      />
      <CollectionPickDialog
        open={collectionTarget !== null}
        onClose={() => setCollectionTarget(null)}
        onPick={handleCollectionPicked}
      />
    </DSBox>
  );
};

export default OrderClocksSection;
